import {
  AppCard,
  AppCardActions,
  AppCardContent,
  AppCardMedia,
} from '@app/components/base/app-card';
import { webApi } from '@mi-front/api-clients';
import { Box, Grid, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { useVideoEditorEditUrl } from '../../hooks/useVideoEditorEditUrl';
import { getCompletedPublishedPlatformsSummary } from '../../lib/get-completed-published-platforms-summary';
import { getEditRenderedVideoUrl } from '../../lib/get-edit-rendered-video-url';
import { isVideoEditorEditRenderCompleted } from '../../lib/is-video-editor-edit-render-completed';
import { EditCardDownloadButton } from './EditCardDownloadButton';
import { EditCardEditorButton } from './EditCardEditorButton';
import { EditCardMediaArea } from './EditCardMediaArea';
import { EditCardOverflowMenu } from './EditCardOverflowMenu';
import { EditCardPublishedPlatformsLabel } from './EditCardPublishedPlatformsLabel';
import { useEditCardDelete } from './useEditCardDelete';

export interface EditCardProps {
  edit: webApi.EnrichedVideoEditorEditListItemDto;
  /** When the edit is generated, optional slot (e.g. fragment-only publish menu). */
  renderPublishControl?: (edit: webApi.EnrichedVideoEditorEditListItemDto) => ReactNode;
  onClone: () => void;
  onDeleteSuccess?: () => Promise<void> | void;
}

/**
 * Card for a single video edition edit: name, status, thumbnail preview, and actions
 * (download next to overflow when generated and URL is ready; otherwise download + edit on the left;
 * optional publish control when generated; when already published to at least one platform, a short
 * summary label is shown and {@link EditCardProps.renderPublishControl} may still render beside it;
 * clone/delete in overflow menu).
 */
export function EditCard({ edit, renderPublishControl, onClone, onDeleteSuccess }: EditCardProps) {
  const editorUrl = useVideoEditorEditUrl(edit.id);
  const { url: renderedDownloadUrl } = getEditRenderedVideoUrl(edit, { mode: 'download' });
  const { url: renderedPlaybackUrl } = getEditRenderedVideoUrl(edit, { mode: 'playback' });
  const hasRenderedVideo = !!renderedDownloadUrl;
  const hasRenderJob = !!edit.renderJob;
  const isGeneratedEdit = isVideoEditorEditRenderCompleted(edit);
  const { count: completedPublishedPlatformCount, singlePlatformKey: singleCompletedPlatformKey } =
    getCompletedPublishedPlatformsSummary(edit.publishedPlatforms);
  const showPublishedPlatformsLabel = isGeneratedEdit && completedPublishedPlatformCount > 0;
  const thumbnailSrc =
    typeof edit.thumbnailUrl === 'string' && edit.thumbnailUrl.length > 0
      ? edit.thumbnailUrl
      : null;

  const { handleDelete } = useEditCardDelete(edit.id, onDeleteSuccess);

  return (
    <Grid item xs={12} sm={6} md={4} lg={4} xl={3}>
      <AppCard
        interactive={false}
        media={
          <AppCardMedia>
            <EditCardMediaArea
              thumbnailSrc={thumbnailSrc}
              renderJob={edit.renderJob}
              publishedPlatforms={edit.publishedPlatforms}
              renderedPlaybackUrl={isGeneratedEdit ? renderedPlaybackUrl : null}
              editorUrl={editorUrl}
              showBlueprintGrid={!isGeneratedEdit}
            />
          </AppCardMedia>
        }
      >
        <AppCardContent sx={{ pb: 0 }}>
          <Typography variant="subtitle1" component="h3">
            {edit.name}
          </Typography>
        </AppCardContent>
        <AppCardActions>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
            {!isGeneratedEdit ? (
              <EditCardEditorButton editorUrl={editorUrl} />
            ) : showPublishedPlatformsLabel ? (
              renderPublishControl ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    minWidth: 0,
                  }}
                >
                  <EditCardPublishedPlatformsLabel
                    completedPlatformCount={completedPublishedPlatformCount}
                    singleCompletedPlatformKey={singleCompletedPlatformKey}
                  />
                  {renderPublishControl(edit)}
                </Box>
              ) : (
                <EditCardPublishedPlatformsLabel
                  completedPlatformCount={completedPublishedPlatformCount}
                  singleCompletedPlatformKey={singleCompletedPlatformKey}
                />
              )
            ) : (
              (renderPublishControl?.(edit) ?? null)
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isGeneratedEdit && renderedDownloadUrl ? (
              <EditCardDownloadButton href={renderedDownloadUrl} />
            ) : null}
            <EditCardOverflowMenu
              onClone={onClone}
              showDelete={!hasRenderedVideo && !hasRenderJob}
              onDelete={handleDelete}
            />
          </Box>
        </AppCardActions>
      </AppCard>
    </Grid>
  );
}
